<template>
  <div>
    <div class="mx-auto max-w-7xl px-5 relative z-10">
      <div
        class="bg-gray-100 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-100/50 p-1.5 mb-6 flex flex-col md:flex-row justify-between items-center"
      >
        <!-- Desktop Tabs Navigation - Modern Design -->
        <div
          class="flex flex-wrap items-center gap-1 bg-white/10 rounded-full p-1 backdrop-blur-sm"
        >
          <template v-for="link in navLinks" :key="link.route">
            <router-link
              :to="{ name: link.route }"
              class="relative px-5 py-2 rounded-full transition-all duration-300"
              :class="{
                'bg-white text-indigo-600 shadow-md border':
                  $route.name === link.route,
                'text-gray-500 hover:bg-gray-200': $route.name !== link.route,
              }"
            >
              <span class="text-sm font-medium">
                {{ link.label }}
              </span>
            </router-link>
          </template>
        </div>

        <!-- Right Side Icons - Enhanced Design -->
        <div class="flex items-center gap-2">
          <!-- Messages Dropdown -->
          <div class="relative dropdown-container">
            <div
              @click="MenuToggle('messages')"
              class="relative p-2.5 rounded-xl bg-blue-500/10 hover:bg-blue-500/20 transition-all duration-200 cursor-pointer backdrop-blur-sm group"
            >
              <p
                class="bg-gradient-to-r from-blue-500 to-blue-700 flex items-center justify-center text-white !w-[18px] !h-[18px] text-[9px] font-bold rounded-full absolute -top-1 -right-1 shadow-lg ring-2 ring-white"
              >
                {{ unreadMessageCount || 0 }}
              </p>
              <svg
                class="w-5 h-5 text-blue-400 group-hover:text-blue-600 transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                />
              </svg>
            </div>

            <!-- Messages Dropdown Content -->
            <transition
              enter-active-class="transition duration-200 ease-out"
              enter-from-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100"
              leave-active-class="transition duration-150 ease-in"
              leave-from-class="transform scale-100 opacity-100"
              leave-to-class="transform scale-95 opacity-0"
            >
              <div
                v-if="toggleMenuName == 'messages'"
                class="absolute right-[-70px] lg:right-0 top-14 lg:top-10 w-64 z-10 bg-white rounded-2xl shadow-lg p-4"
              >
                <div class="font-bold text-sm pb-3 flex items-center gap-2">
                  Messages
                  <p
                    class="bg-primary flex items-center justify-center text-white !w-[25px] !h-[25px] text-xs rounded-full"
                    v-if="unreadMessageCount != 0"
                    changed
                  >
                    {{ unreadMessageCount }}
                  </p>
                </div>
                <div class="flex flex-col gap-2">
                  <div
                    v-if="messageList.length != 0"
                    class="flex flex-col gap-2"
                  >
                    <router-link
                      :to="{ name: 'PreviewStudentMessageList' }"
                      class="flex flex-col border gap-1 py-2 px-4 rounded-lg hover:bg-gray-100"
                      :class="{
                        'bg-white border-blue-500 shadow-md ring-1 ring-blue-100 hover:bg-blue-50':
                          !message.readed,
                        'bg-white border-gray-300 hover:bg-gray-100 hover:border-gray-300':
                          message.readed,
                      }"
                      v-for="(message, index) in messageList.slice(0, 3)"
                      :key="message.id || index"
                    >
                      <h4
                        class="text-xs truncate transition-colors uppercase font-bold"
                        :class="
                          !message.readed
                            ? 'font-bold text-slate-900'
                            : 'font-medium text-slate-600'
                        "
                      >
                        {{ message.owner_display_name || "NA" }}
                      </h4>
                      <div
                        class="text-[12px] tracking-tight flex w-full"
                        :class="
                          !message.readed
                            ? 'text-slate-700 font-medium'
                            : 'text-slate-500 font-normal'
                        "
                      >
                        <p class="line-clamp-1 w-full">
                          {{ message.course_title || "NA" }}
                        </p>
                      </div>
                      <p
                        class="text-xs line-clamp-1"
                        :class="
                          !message.readed
                            ? 'text-slate-700 font-medium'
                            : 'text-slate-500 font-normal'
                        "
                      >
                        {{ message.text || "No preview available..." }}
                      </p>
                      <p
                        class="text-xs"
                        :class="
                          !message.readed
                            ? 'text-slate-700 font-medium'
                            : 'text-slate-500 font-normal'
                        "
                      >
                        {{ formatDateTime(message.created_at) }}
                      </p>
                    </router-link>
                    <router-link
                      :to="{ name: 'PreviewStudentMessageList' }"
                      v-if="messageList.length > 3"
                      class="text-indigo-600 font-medium text-xs py-3 text-center hover:bg-indigo-50 transition-colors border-t border-gray-100"
                    >
                      View All Messages →
                    </router-link>
                  </div>
                  <div v-else class="text-center py-12 text-gray-400 text-sm">
                    <svg
                      class="w-12 h-12 mx-auto mb-3 text-gray-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                      />
                    </svg>
                    No messages found
                  </div>
                </div>
              </div>
            </transition>
          </div>

          <!-- Notifications Dropdown -->
          <div class="relative dropdown-container">
            <div
              @click="MenuToggle('notification')"
              class="relative p-2.5 rounded-xl bg-blue-500/10 hover:bg-blue-500/20 transition-all duration-200 cursor-pointer backdrop-blur-sm group"
            >
              <p
                class="bg-gradient-to-r from-blue-500 to-blue-700 flex items-center justify-center text-white !w-[18px] !h-[18px] text-[9px] font-bold rounded-full absolute -top-1 -right-1 shadow-lg ring-2 ring-white"
              >
                {{ unread_count || "0" }}
              </p>
              <svg
                class="w-5 h-5 text-blue-400 group-hover:text-blue-600 transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
            </div>

            <!-- Notifications Dropdown Content -->
            <transition
              enter-active-class="transition duration-200 ease-out"
              enter-from-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100"
              leave-active-class="transition duration-150 ease-in"
              leave-from-class="transform scale-100 opacity-100"
              leave-to-class="transform scale-95 opacity-0"
            >
              <div
                v-if="toggleMenuName == 'notification'"
                class="absolute right-0 top-12 w-80 z-20 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
              >
                <div class="bg-gradient-to-r bg-blues px-4 py-3.5">
                  <div
                    class="font-semibold text-sm text-white flex items-center gap-2"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                      />
                    </svg>
                    Notifications
                    <span
                      class="bg-blue-600 px-2 py-0.5 rounded-full text-xs"
                      >{{ unread_count || 0 }}</span
                    >
                  </div>
                </div>
                <div class="max-h-80 overflow-y-auto divide-y divide-gray-100">
                  <div
                    v-if="notificationlist?.data?.length != 0"
                    class="flex flex-col"
                  >
                    <router-link
                      :to="{ name: 'PreviewStudentNotifications' }"
                      class="flex items-start gap-2 p-3.5 hover:bg-gray-50 transition-colors"
                      v-for="notifi in notificationlist.data.slice(0, 3)"
                      :key="notifi.id"
                    >
                      <!-- <div
                        class="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0"
                      >
                        <svg
                          class="w-4 h-4 text-purple-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div> -->
                      <div class="flex-1">
                        <p
                          class="font-medium text-sm text-gray-800 line-clamp-2"
                        >
                          {{ notifi.topic }}
                        </p>
                        <p class="text-xs text-gray-400 mt-1">
                          {{ formatDate(notifi.created_at) }}
                        </p>
                      </div>
                    </router-link>
                    <router-link
                      :to="{ name: 'PreviewStudentNotifications' }"
                      v-if="notificationlist.data.length > 3"
                      class="text-blues font-medium text-xs py-3 text-center hover:text-blue-600 transition-colors border-t border-gray-100"
                    >
                      View All →
                    </router-link>
                  </div>
                  <div v-else class="text-center py-12 text-gray-400 text-sm">
                    <svg
                      class="w-12 h-12 mx-auto mb-3 text-gray-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                      />
                    </svg>
                    No notifications
                  </div>
                </div>
              </div>
            </transition>
          </div>

          <!-- Profile Dropdown - Modern Design -->
          <div class="relative dropdown-container">
            <button
              @click="MenuToggle('profile')"
              class="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full bg-blue-500/10 hover:bg-blue-500/20 transition-all duration-200 group"
            >
              <img
                :src="userInfo?.image || '@/assets/new/image.png'"
                class="w-8 h-8 rounded-full border-2 border-white/50 group-hover:border-white transition-all"
                :onerror="`this.src='${require('@/assets/new/image.png')}'`"
              />
              <svg
                class="w-4 h-4 text-blue-400 group-hover:text-blue-600 transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            <!-- Profile Dropdown Content -->
            <transition
              enter-active-class="transition duration-200 ease-out"
              enter-from-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100"
              leave-active-class="transition duration-150 ease-in"
              leave-from-class="transform scale-100 opacity-100"
              leave-to-class="transform scale-95 opacity-0"
            >
              <div
                v-if="toggleMenuName == 'profile'"
                class="absolute right-0 mt-3 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
              >
                <!-- Profile Header -->
                <!-- <div
                  class="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-5 text-white"
                >
                  <div class="flex items-center gap-3">
                    <img
                      v-if="
                        userInfo && userInfo.image && userInfo.image != null
                      "
                      :src="userInfo.image"
                      class="w-14 h-14 rounded-full border-3 border-white shadow-lg object-cover"
                    />
                    <img
                      v-else
                      src="@/assets/new/image.png"
                      class="w-14 h-14 rounded-full border-3 border-white shadow-lg object-cover"
                    />
                    <div>
                      <p class="font-semibold text-base">
                        {{ userInfo?.user?.display_name || "Student" }}
                      </p>
                      <p class="text-xs text-white/80 mt-0.5">
                        PRN: {{ userInfo?.prn_number || "NA" }}
                      </p>
                      <p class="text-xs text-white/70 mt-0.5">
                        {{ userInfo?.email || "" }}
                      </p>
                    </div>
                  </div>
                </div> -->

                <div class="p-2">
                  <router-link
                    :to="{ name: 'PreviewStudentUserProfile' }"
                    class="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
                    @click="toggleMenuName = ''"
                  >
                    <div
                      class="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center"
                    >
                      <svg
                        class="w-4 h-4 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    </div>
                    <span class="text-sm text-gray-700">My Profile</span>
                  </router-link>

                  <router-link
                    :to="{ name: 'PreviewStudentTaskList' }"
                    class="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
                    @click="toggleMenuName = ''"
                  >
                    <div
                      class="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center"
                    >
                      <svg
                        class="w-4 h-4 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                        />
                      </svg>
                    </div>
                    <span class="text-sm text-gray-700">My Tasks</span>
                  </router-link>

                  <router-link
                    :to="{ name: 'PreviewStudentSyallbusView' }"
                    class="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
                    @click="toggleMenuName = ''"
                  >
                    <div
                      class="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center"
                    >
                      <svg
                        class="w-4 h-4 text-purple-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                        />
                      </svg>
                    </div>
                    <span class="text-sm text-gray-700">User Instructions</span>
                  </router-link>

                  <div class="border-t border-gray-100 my-2"></div>

                  <a
                    href="https://au-exams.galecta.com"
                    class="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
                    target="_blank"
                  >
                    <div
                      class="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center"
                    >
                      <svg
                        class="w-4 h-4 text-orange-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <span class="text-sm text-gray-700">Support Tickets</span>
                  </a>

                  <a
                    href="https://onlineexams.alliance.edu.in"
                    class="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
                    target="_blank"
                  >
                    <div
                      class="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center"
                    >
                      <svg
                        class="w-4 h-4 text-red-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                    <span class="text-sm text-gray-700">Semester Exam</span>
                  </a>
                </div>
              </div>
            </transition>
          </div>

          <!-- Mobile Menu Button -->
          <div class="lg:hidden relative dropdown-container">
            <button
              @click="MenuToggle('mobilemenu')"
              class="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 transition-all duration-200"
            >
              <svg
                class="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>

            <!-- Mobile Menu Content -->
            <transition
              enter-active-class="transition duration-200 ease-out"
              enter-from-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100"
              leave-active-class="transition duration-150 ease-in"
              leave-from-class="transform scale-100 opacity-100"
              leave-to-class="transform scale-95 opacity-0"
            >
              <div
                v-if="toggleMenuName == 'mobilemenu'"
                class="absolute right-0 top-12 w-64 z-20 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
              >
                <div class="p-2">
                  <router-link
                    :to="{ name: 'PreviewStudentHome' }"
                    class="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
                    :class="{
                      'bg-indigo-50 text-indigo-600':
                        $route.name === 'PreviewStudentHome',
                    }"
                    @click="toggleMenuName = ''"
                  >
                    <div
                      class="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center"
                    >
                      <svg
                        class="w-4 h-4 text-indigo-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                        />
                      </svg>
                    </div>
                    <span class="text-sm font-medium">Home</span>
                  </router-link>
                  <router-link
                    :to="{ name: 'PreviewStudentCourseProgress' }"
                    class="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
                    :class="{
                      'bg-indigo-50 text-indigo-600':
                        $route.name === 'PreviewStudentCourseProgress',
                    }"
                    @click="toggleMenuName = ''"
                  >
                    <div
                      class="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center"
                    >
                      <svg
                        class="w-4 h-4 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                        />
                      </svg>
                    </div>
                    <span class="text-sm font-medium">My Courses</span>
                  </router-link>
                  <router-link
                    :to="{ name: 'StudentBlogs' }"
                    class="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
                    :class="{
                      'bg-indigo-50 text-indigo-600':
                        $route.name === 'StudentBlogs',
                    }"
                    @click="toggleMenuName = ''"
                  >
                    <div
                      class="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center"
                    >
                      <svg
                        class="w-4 h-4 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                        />
                      </svg>
                    </div>
                    <span class="text-sm font-medium">Blogs</span>
                  </router-link>
                  <router-link
                    :to="{ name: 'CompleteCourse' }"
                    class="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
                    :class="{
                      'bg-indigo-50 text-indigo-600':
                        $route.name === 'CompleteCourse',
                    }"
                    @click="toggleMenuName = ''"
                  >
                    <div
                      class="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center"
                    >
                      <svg
                        class="w-4 h-4 text-purple-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <span class="text-sm font-medium">Complete Course</span>
                  </router-link>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import axios from "axios";
import Logout from "@/components/Logout.vue";
import rightClickProtection from "@/mixins/rightClickProtection";

export default {
  mixins: [rightClickProtection],

  computed: {
    ...mapGetters("student", ["studentdata"]),
    ...mapGetters(["isAuthenticated", "permissionList", "messageCount"]),

    // ✅ Derive unread message count from local messageList
    unreadMessageCount() {
      return this.messageList.filter((m) => !m.readed).length;
    },
  },

  data() {
    return {
      logo_image: require(`${process.env.VUE_APP_COMS_LOGO}`),
      toggleMenuName: null,
      profileInfo: null,
      LogOutModal: false,
      notificationlist: { data: [] }, // ✅ initialize as object with data array to avoid template errors
      unread_count: 0,
      messageList: [],
      navLinks: [
        { label: "Home", route: "PreviewStudentHome" },
        { label: "My Courses", route: "PreviewMyCourse" },
        { label: "Blogs", route: "PreviewStudentBlogs" },
        { label: "Complete Course", route: "PreviewCompleteCourse" },
      ],
    };
  },

  components: { Logout },

  created() {
    this.getMessages(1);
    this.getNotifications();
    if (this.studentdata) {
      axios
        .get(
          `${process.env.VUE_APP_API}/profile_detail/?Key=${this.studentdata.studentToken}&format=json`,
        )
        .then((response) => (this.profileInfo = response.data));
    }
  },

  mounted() {
    document.addEventListener("click", this.handleClickOutside);
    // this.$store.dispatch("connectMessageSocket");
    // this.$store.commit("SET_ON_NEW_MESSAGE", () => {
    //   this.loadMessages(this.currentPage);
    // });
    // this.loadMessages();
  },

  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },

  methods: {
    ...mapActions(["logOutUser"]),
    ...mapMutations(["notifyCount"]),

    async getMessages(page) {
      await axios
        .get(
          `${process.env.VUE_APP_API}/message-course-overall-details/?client__id=${this.studentdata.studentId}&Key=${this.studentdata.studentToken}&page=${page}`,
        )
        .then((response) => {
          if (response) {
            // this.notifyCount(response.data.count);
            this.totalPages = response.data.data.total_page;
            this.messageList = response.data.data.data;
            this.total_count = response.data.data.total;
            console.log("total_count", this.total_count);
            console.log("notificationList", this.messageList);
          }
        });
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString("en-IN");
    },

    formatDateTime(dateString) {
      const date = new Date(dateString);
      const now = new Date();
      const isSameDate =
        date.getDate() === now.getDate() &&
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear();

      if (isSameDate) {
        return date.toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
      } else {
        return date.toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
      }
    },

    async getNotifications() {
      if (!this.studentdata) return;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/notifications/${this.studentdata.studentId}/?Key=${this.studentdata.studentToken}`,
        );
        if (response?.data?.status === 200) {
          this.notificationlist = response.data.data;
          this.unread_count = response.data.count;
        }
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
      }
    },

    checkinTheList(feature) {
      return this.permissionList.find((x) => x === feature) !== undefined;
    },

    MenuToggle(name) {
      this.toggleMenuName = this.toggleMenuName === name ? null : name;
    },

    async logOut() {
      await this.logOutUser();
      this.LogOutModal = false;
      this.$router.push({ name: "Login" });
    },

    handleClickOutside(event) {
      if (!event.target.closest(".dropdown-container")) {
        this.toggleMenuName = null;
      }
    },
  },
};
</script>

<style></style>
